import React, { useState } from 'react';
import { Bell, User, Search, Plus, Edit, Download, Trash2, X, ChevronRight, Filter, MoreVertical, Eye, Calendar, TrendingUp, Package, Users, DollarSign, BarChart3, Grid3X3, List, Settings, HelpCircle } from 'lucide-react';
import logo from '../assets/infraLogo.jpg';
const InventoryDashboard = () => {
  const [activeTab, setActiveTab] = useState('master');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [skinColor, setSkinColor] = useState('corporate-blue');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubDropdown, setActiveSubDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('table');
  const [showFilters, setShowFilters] = useState(false);
  const [showThemeDropdown, setShowThemeDropdown] = useState(false);
  const [formData, setFormData] = useState({
    categoryCode: '',
    categoryName: '',
    remarks: ''
  });

  // Enhanced sample data with more realistic information
  const [categories, setCategories] = useState([
    { id: 1, sno: 1, categoryCode: 'ELEC001', categoryName: 'Electronics', remarks: 'Consumer electronics and tech accessories', status: 'Active', createdDate: '2024-01-15', itemCount: 245 },
    { id: 2, sno: 2, categoryCode: 'CLTH002', categoryName: 'Clothing & Apparel', remarks: 'Fashion items and clothing accessories', status: 'Active', createdDate: '2024-01-20', itemCount: 189 },
    { id: 3, sno: 3, categoryCode: 'BOOK003', categoryName: 'Books & Media', remarks: 'Educational and entertainment books', status: 'Active', createdDate: '2024-02-01', itemCount: 156 },
    { id: 4, sno: 4, categoryCode: 'HOME004', categoryName: 'Home & Garden', remarks: 'Home improvement and gardening supplies', status: 'Active', createdDate: '2024-02-10', itemCount: 298 },
    { id: 5, sno: 5, categoryCode: 'SPRT005', categoryName: 'Sports & Recreation', remarks: 'Sports equipment and outdoor gear', status: 'Active', createdDate: '2024-02-15', itemCount: 134 },
    { id: 6, sno: 6, categoryCode: 'AUTO006', categoryName: 'Automotive', remarks: 'Car parts and automotive accessories', status: 'Inactive', createdDate: '2024-03-01', itemCount: 78 },
  ]);

  const itemsPerPage = 5;
  const totalPages = Math.ceil(categories.length / itemsPerPage);

  const skinColors = [
    { 
      value: 'corporate-blue', 
      label: 'Corporate Blue', 
      primary: 'bg-blue-600', 
      secondary: 'bg-blue-50', 
      accent: 'text-blue-600',
      colorDot: 'bg-blue-600',
      description: 'Professional blue theme'
    },
    { 
      value: 'executive-green', 
      label: 'Executive Green', 
      primary: 'bg-emerald-600', 
      secondary: 'bg-emerald-50', 
      accent: 'text-emerald-600',
      colorDot: 'bg-emerald-600',
      description: 'Modern green theme'
    },
    { 
      value: 'modern-purple', 
      label: 'Modern Purple', 
      primary: 'bg-purple-600', 
      secondary: 'bg-purple-50', 
      accent: 'text-purple-600',
      colorDot: 'bg-purple-600',
      description: 'Creative purple theme'
    },
    { 
      value: 'professional-gray', 
      label: 'Professional Gray', 
      primary: 'bg-slate-700', 
      secondary: 'bg-slate-50', 
      accent: 'text-slate-600',
      colorDot: 'bg-slate-700',
      description: 'Classic gray theme'
    },
    { 
      value: 'sunset-orange', 
      label: 'Sunset Orange', 
      primary: 'bg-orange-600', 
      secondary: 'bg-orange-50', 
      accent: 'text-orange-600',
      colorDot: 'bg-orange-600',
      description: 'Vibrant orange theme'
    },
    { 
      value: 'royal-navy', 
      label: 'Royal Navy', 
      primary: 'bg-indigo-800', 
      secondary: 'bg-indigo-50', 
      accent: 'text-indigo-700',
      colorDot: 'bg-indigo-800',
      description: 'Elegant navy theme'
    },
    { 
      value: 'dark-mode', 
      label: 'Dark Professional', 
      primary: 'bg-gray-900', 
      secondary: 'bg-gray-800', 
      accent: 'text-gray-300',
      colorDot: 'bg-gray-900',
      description: 'Dark mode theme'
    }
  ];

  const menuItems = [
    {
      id: 'master',
      label: 'Inventory Master',
      icon: Package,
      description: 'Manage core inventory data',
      submenus: [
        {
          id: 'item-code-generation',
          label: 'Item Code Generation',
          icon: '🔢',
          description: 'Generate and manage item codes',
          hoverItems: [
            { id: 'item-category', label: 'Item Category', icon: '📁', description: 'Manage item categories' },
            { id: 'branch-items', label: 'Branch Items', icon: '🏢', description: 'Branch-specific items' },
            { id: 'branch-item-stock-update', label: 'Branch Item Stock Update', icon: '📊', description: 'Update branch stock levels' }
          ]
        },
        {
          id: 'supplier',
          label: 'Supplier Management',
          icon: '🏭',
          description: 'Manage supplier information',
          hoverItems: [
            { id: 'supplier-group-master', label: 'Supplier Group Master', icon: '👥', description: 'Manage supplier groups' },
            { id: 'supplier-master', label: 'Supplier Master', icon: '🏭', description: 'Main supplier database' },
            { id: 'supplier-with-tax', label: 'Supplier with Tax', icon: '💰', description: 'Tax-enabled suppliers' },
            { id: 'supplier-items', label: 'Supplier Items', icon: '📦', description: 'Items by supplier' },
            { id: 'supplier-mapping', label: 'Supplier Mapping', icon: '🗺️', description: 'Map suppliers to locations' }
          ]
        },
        {
          id: 'other-master',
          label: 'Other Masters',
          icon: '⚙️',
          description: 'Additional configuration',
          hoverItems: [
            { id: 'uom-master', label: 'UOM Master', icon: '📏', description: 'Units of measurement' },
            { id: 'department-master', label: 'Department Master', icon: '🏬', description: 'Department management' },
            { id: 'transport-master', label: 'Transport Master', icon: '🚛', description: 'Transportation setup' },
            { id: 'accounts-group-master', label: 'Accounts Group Master', icon: '📊', description: 'Account grouping' },
            { id: 'account-group-type', label: 'Account Group Type', icon: '📋', description: 'Account categorization' },
            { id: 'ledger-master', label: 'Ledger Master', icon: '📖', description: 'Ledger management' },
            { id: 'store-master', label: 'Store Master', icon: '🏪', description: 'Store configuration' },
            { id: 'employee-details', label: 'Employee Details', icon: '👤', description: 'Employee database' }
          ]
        }
      ]
    },
    {
      id: 'transaction',
      label: 'Transactions',
      icon: BarChart3,
      description: 'Process inventory transactions',
      submenus: [
        { id: 'purchase-orders', label: 'Purchase Orders', icon: '🛒', description: 'Manage purchase orders' },
        { id: 'sales-orders', label: 'Sales Orders', icon: '💰', description: 'Process sales orders' },
        { id: 'stock-transfer', label: 'Stock Transfer', icon: '🔄', description: 'Transfer stock between locations' },
        { id: 'stock-adjustment', label: 'Stock Adjustment', icon: '⚖️', description: 'Adjust stock levels' },
        { id: 'goods-receipt', label: 'Goods Receipt', icon: '📥', description: 'Receive incoming goods' },
        { id: 'goods-issue', label: 'Goods Issue', icon: '📤', description: 'Issue goods from inventory' }
      ]
    },
    {
      id: 'report',
      label: 'Reports & Analytics',
      icon: TrendingUp,
      description: 'Generate insights and reports',
      submenus: [
        { id: 'stock-reports', label: 'Stock Reports', icon: '📊', description: 'Current stock status' },
        { id: 'transaction-reports', label: 'Transaction Reports', icon: '📋', description: 'Transaction history' },
        { id: 'valuation-reports', label: 'Valuation Reports', icon: '💎', description: 'Inventory valuation' },
        { id: 'analytics', label: 'Analytics', icon: '🔍', description: 'Advanced analytics' },
        { id: 'dashboards', label: 'Dashboards', icon: '📱', description: 'Visual dashboards' }
      ]
    }
  ];

  // Stats for dashboard overview
  const dashboardStats = [
    { title: 'Total Categories', value: '24', change: '+12%', icon: Package, color: 'blue' },
    { title: 'Active Items', value: '1,234', change: '+8%', icon: Grid3X3, color: 'green' },
    { title: 'Total Value', value: '$2.4M', change: '+15%', icon: DollarSign, color: 'purple' },
    { title: 'Suppliers', value: '89', change: '+5%', icon: Users, color: 'orange' }
  ];

  const getThemeClasses = (theme) => {
    const themes = {
      'corporate-blue': {
        bg: 'bg-gradient-to-br from-blue-100 via-blue-50 to-white',
        headerBg: 'bg-blue-600/95 backdrop-blur-sm',
        navBg: 'bg-gradient-to-r from-blue-600 to-blue-700',
        cardBg: 'bg-white/90 backdrop-blur-sm',
        text: 'text-gray-800',
        headerText: 'text-white',
        navText: 'text-white',
        accent: 'text-blue-600',
        border: 'border-blue-200/50',
        hover: 'hover:bg-blue-700',
        activeTab: 'bg-blue-800',
        button: 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800',
        shadow: 'shadow-blue-500/10',
        tableHead: 'bg-gradient-to-r from-blue-500 to-blue-600',
        tableHeadText: 'text-white'
      },
      'executive-green': {
        bg: 'bg-gradient-to-br from-emerald-100 via-emerald-50 to-white',
        headerBg: 'bg-emerald-600/95 backdrop-blur-sm',
        navBg: 'bg-gradient-to-r from-emerald-600 to-emerald-700',
        cardBg: 'bg-white/90 backdrop-blur-sm',
        text: 'text-gray-800',
        headerText: 'text-white',
        navText: 'text-white',
        accent: 'text-emerald-600',
        border: 'border-emerald-200/50',
        hover: 'hover:bg-emerald-700',
        activeTab: 'bg-emerald-800',
        button: 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800',
        shadow: 'shadow-emerald-500/10',
        tableHead: 'bg-gradient-to-r from-emerald-500 to-emerald-600',
        tableHeadText: 'text-white'
      },
      'modern-purple': {
        bg: 'bg-gradient-to-br from-purple-100 via-purple-50 to-white',
        headerBg: 'bg-purple-600/95 backdrop-blur-sm',
        navBg: 'bg-gradient-to-r from-purple-600 to-purple-700',
        cardBg: 'bg-white/90 backdrop-blur-sm',
        text: 'text-gray-800',
        headerText: 'text-white',
        navText: 'text-white',
        accent: 'text-purple-600',
        border: 'border-purple-200/50',
        hover: 'hover:bg-purple-700',
        activeTab: 'bg-purple-800',
        button: 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800',
        shadow: 'shadow-purple-500/10',
        tableHead: 'bg-gradient-to-r from-purple-500 to-purple-600',
        tableHeadText: 'text-white'
      },
      'professional-gray': {
        bg: 'bg-gradient-to-br from-slate-100 via-slate-50 to-white',
        headerBg: 'bg-slate-700/95 backdrop-blur-sm',
        navBg: 'bg-gradient-to-r from-slate-700 to-slate-800',
        cardBg: 'bg-white/90 backdrop-blur-sm',
        text: 'text-gray-800',
        headerText: 'text-white',
        navText: 'text-white',
        accent: 'text-slate-600',
        border: 'border-slate-200/50',
        hover: 'hover:bg-slate-800',
        activeTab: 'bg-slate-900',
        button: 'bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900',
        shadow: 'shadow-slate-500/10',
        tableHead: 'bg-gradient-to-r from-slate-600 to-slate-700',
        tableHeadText: 'text-white'
      },
      'sunset-orange': {
        bg: 'bg-gradient-to-br from-orange-100 via-orange-50 to-white',
        headerBg: 'bg-orange-600/95 backdrop-blur-sm',
        navBg: 'bg-gradient-to-r from-orange-600 to-orange-700',
        cardBg: 'bg-white/90 backdrop-blur-sm',
        text: 'text-gray-800',
        headerText: 'text-white',
        navText: 'text-white',
        accent: 'text-orange-600',
        border: 'border-orange-200/50',
        hover: 'hover:bg-orange-700',
        activeTab: 'bg-orange-800',
        button: 'bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800',
        shadow: 'shadow-orange-500/10',
        tableHead: 'bg-gradient-to-r from-orange-500 to-orange-600',
        tableHeadText: 'text-white'
      },
      'royal-navy': {
        bg: 'bg-gradient-to-br from-indigo-100 via-indigo-50 to-white',
        headerBg: 'bg-indigo-800/95 backdrop-blur-sm',
        navBg: 'bg-gradient-to-r from-indigo-800 to-indigo-900',
        cardBg: 'bg-white/90 backdrop-blur-sm',
        text: 'text-gray-800',
        headerText: 'text-white',
        navText: 'text-white',
        accent: 'text-indigo-700',
        border: 'border-indigo-200/50',
        hover: 'hover:bg-indigo-900',
        activeTab: 'bg-indigo-950',
        button: 'bg-gradient-to-r from-indigo-800 to-indigo-900 hover:from-indigo-900 hover:to-indigo-950',
        shadow: 'shadow-indigo-500/10',
        tableHead: 'bg-gradient-to-r from-indigo-700 to-indigo-800',
        tableHeadText: 'text-white'
      },
      'dark-mode': {
        bg: 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900',
        headerBg: 'bg-gray-800/95 backdrop-blur-sm',
        navBg: 'bg-gradient-to-r from-gray-900 to-black',
        cardBg: 'bg-gray-800/90 backdrop-blur-sm',
        text: 'text-gray-100',
        headerText: 'text-gray-100',
        navText: 'text-gray-100',
        accent: 'text-gray-300',
        border: 'border-gray-700/50',
        hover: 'hover:bg-black',
        activeTab: 'bg-black',
        button: 'bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900',
        shadow: 'shadow-gray-900/20',
        tableHead: 'bg-gradient-to-r from-gray-700 to-gray-800',
        tableHeadText: 'text-gray-100'
      }
    };
    return themes[theme] || themes['corporate-blue'];
  };

  const currentTheme = getThemeClasses(skinColor);

  const filteredCategories = categories.filter(category =>
    category.categoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.categoryCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedCategories = filteredCategories.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedItems(paginatedCategories.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      setSelectedItems([...selectedItems, id]);
    } else {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    }
  };

  const handleAddCategory = () => {
    const newCategory = {
      id: categories.length + 1,
      sno: categories.length + 1,
      categoryCode: formData.categoryCode,
      categoryName: formData.categoryName,
      remarks: formData.remarks,
      status: 'Active',
      createdDate: new Date().toISOString().split('T')[0],
      itemCount: 0
    };
    setCategories([...categories, newCategory]);
    setFormData({ categoryCode: '', categoryName: '', remarks: '' });
    setShowAddForm(false);
  };

  const handleRemoveSelected = () => {
    setCategories(categories.filter(category => !selectedItems.includes(category.id)));
    setSelectedItems([]);
  };

  const StatCard = ({ stat }) => {
    const iconColors = {
      blue: 'text-blue-600 bg-blue-100',
      green: 'text-emerald-600 bg-emerald-100',
      purple: 'text-purple-600 bg-purple-100',
      orange: 'text-orange-600 bg-orange-100'
    };

    return (
      <div className={`${currentTheme.cardBg} border ${currentTheme.border} rounded-2xl p-6 ${currentTheme.shadow} shadow-xl hover:shadow-2xl transition-all duration-300 group hover:scale-105`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm font-medium ${currentTheme.accent} mb-1`}>{stat.title}</p>
            <p className={`text-3xl font-bold ${currentTheme.text} group-hover:scale-110 transition-transform duration-300`}>{stat.value}</p>
            <p className="text-sm text-green-600 font-semibold mt-1">{stat.change}</p>
          </div>
          <div className={`p-4 rounded-2xl ${iconColors[stat.color]} group-hover:scale-110 transition-transform duration-300`}>
            <stat.icon className="w-8 h-8" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${currentTheme.bg}`}>
      {/* Enhanced Header */}
      <header className={`${currentTheme.headerBg} border-b ${currentTheme.border} px-6 py-4 shadow-lg sticky top-0 z-30`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-4">
              <div className={`w-12 h-12 ${currentTheme.button} rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg`}>
              <img 
                                  src={logo} 
                                  alt="Infraconsoft Logo" 
                                  className="w-full h-full object-cover object-center"
                                  onError={(e) => {
                                    // Fallback if image doesn't load
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
              </div>
              <div>
                <h1 className={`text-2xl font-bold ${currentTheme.headerText}`}>
                  Infraconsoft
                </h1>
                <p className={`text-sm ${currentTheme.headerText} opacity-80 font-medium`}>Inventory Management System</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Global Search */}
            <div className="relative group">
              <Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${currentTheme.headerText} opacity-80 group-focus-within:text-white transition-colors`} />
              <input
                type="text"
                placeholder="Global search..."
                className={`pl-12 pr-4 py-3 w-80 border border-white/20 bg-white/10 ${currentTheme.headerText} placeholder-white/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/30 shadow-sm transition-all duration-200 focus:shadow-lg backdrop-blur-sm`}
              />
            </div>

            {/* Enhanced Theme Selector */}
            <div className="relative">
              <button
                onClick={() => setShowThemeDropdown(!showThemeDropdown)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-xl border border-white/20 bg-white/10 ${currentTheme.headerText} focus:outline-none focus:ring-2 focus:ring-white/30 shadow-sm font-medium min-w-[200px] backdrop-blur-sm transition-all duration-200 hover:bg-white/20`}
              >
                <div className={`w-4 h-4 rounded-full ${skinColors.find(c => c.value === skinColor)?.colorDot} shadow-sm border border-white/30`}></div>
                <span className="flex-1 text-left">{skinColors.find(c => c.value === skinColor)?.label}</span>
                <svg 
                  className={`w-4 h-4 transition-transform duration-200 ${showThemeDropdown ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Enhanced Dropdown Menu */}
              {showThemeDropdown && (
                <>
                  {/* Backdrop to close dropdown when clicking outside */}
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setShowThemeDropdown(false)}
                  ></div>
                  
                  <div className="absolute top-full right-0 mt-2 w-72 bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 z-50 overflow-hidden">
                    <div className="p-2">
                      <div className="px-4 py-3 border-b border-gray-200/50">
                        <h3 className="text-sm font-bold text-gray-800">Choose Theme</h3>
                        <p className="text-xs text-gray-600 mt-1">Select your preferred color scheme</p>
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {skinColors.map((color) => (
                          <button
                            key={color.value}
                            onClick={() => {
                              setSkinColor(color.value);
                              setShowThemeDropdown(false);
                            }}
                            className={`w-full flex items-center space-x-4 px-4 py-3 hover:bg-gray-50 transition-all duration-200 text-left group ${
                              skinColor === color.value ? 'bg-blue-50 border border-blue-200 rounded-xl mx-1 my-1' : 'rounded-xl mx-1 my-1'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <div className={`w-6 h-6 rounded-full ${color.colorDot} shadow-md group-hover:scale-110 transition-transform duration-200 border-2 border-white`}></div>
                              <div className="flex-1">
                                <div className={`font-semibold text-gray-800 group-hover:text-gray-900 ${skinColor === color.value ? 'text-blue-700' : ''}`}>
                                  {color.label}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {color.description}
                                </div>
                              </div>
                            </div>
                            {skinColor === color.value && (
                              <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Notifications */}
            <button className={`relative p-3 rounded-xl bg-white/10 hover:bg-white/20 shadow-sm transition-all duration-200 hover:shadow-lg group backdrop-blur-sm`}>
              <Bell className={`w-6 h-6 ${currentTheme.headerText} group-hover:scale-110 transition-transform`} />
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold animate-pulse">3</span>
            </button>

            {/* Settings */}
            <button className={`p-3 rounded-xl bg-white/10 hover:bg-white/20 shadow-sm transition-all duration-200 hover:shadow-lg group backdrop-blur-sm`}>
              <Settings className={`w-6 h-6 ${currentTheme.headerText} group-hover:rotate-90 transition-transform duration-300`} />
            </button>

            {/* Help */}
            <button className={`p-3 rounded-xl bg-white/10 hover:bg-white/20 shadow-sm transition-all duration-200 hover:shadow-lg group backdrop-blur-sm`}>
              <HelpCircle className={`w-6 h-6 ${currentTheme.headerText} group-hover:scale-110 transition-transform`} />
            </button>

            {/* Profile */}
            <button className={`flex items-center space-x-3 p-3 rounded-xl bg-white/10 hover:bg-white/20 shadow-sm transition-all duration-200 hover:shadow-lg group backdrop-blur-sm`}>
              <div className={`w-10 h-10 ${currentTheme.button} rounded-xl flex items-center justify-center text-white font-bold shadow-md`}>
                A
              </div>
              <div className="text-left hidden lg:block">
                <div className={`text-sm font-bold ${currentTheme.headerText}`}>Admin User</div>
                <div className={`text-xs ${currentTheme.headerText} opacity-80 font-medium`}>Administrator</div>
              </div>
              <ChevronRight className={`w-4 h-4 ${currentTheme.headerText} opacity-80 group-hover:translate-x-1 transition-transform`} />
            </button>
          </div>
        </div>
      </header>

      {/* Enhanced Navigation */}
      <nav className={`${currentTheme.navBg} shadow-2xl relative z-20`}>
        <div className="px-6">
          <div className="flex space-x-2">
            {menuItems.map(item => (
              <div 
                key={item.id} 
                className="relative group"
                onMouseEnter={() => {
                  setActiveDropdown(item.id);
                  setActiveSubDropdown(null);
                }}
                onMouseLeave={() => {
                  setActiveDropdown(null);
                  setActiveSubDropdown(null);
                }}
              >
                <button
                  onClick={() => setActiveTab(item.id)}
                  className={`flex items-center space-x-3 px-8 py-5 font-semibold transition-all duration-300 ${
                    activeTab === item.id
                      ? `${currentTheme.activeTab} text-white shadow-2xl scale-105`
                      : `${currentTheme.navText} ${currentTheme.hover} hover:scale-105`
                  } rounded-t-2xl relative overflow-hidden group`}
                >
                  <item.icon className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                  <span className="whitespace-nowrap">{item.label}</span>
                  <svg 
                    className={`w-4 h-4 transition-transform duration-300 ${activeDropdown === item.id ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  {activeTab === item.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-white rounded-full shadow-lg"></div>
                  )}
                </button>

                {/* Enhanced Dropdown Menu */}
                <div className={`absolute top-full left-0 w-80 ${currentTheme.cardBg} rounded-b-2xl rounded-tr-2xl shadow-2xl border ${currentTheme.border} transform transition-all duration-400 ${
                  activeDropdown === item.id 
                    ? 'opacity-100 visible translate-y-0 scale-100' 
                    : 'opacity-0 invisible -translate-y-4 scale-95'
                }`}>
                  <div className="p-2">
                    {item.submenus.map((submenu, index) => (
                      <div
                        key={submenu.id}
                        className="relative"
                        onMouseEnter={() => submenu.hoverItems && setActiveSubDropdown(submenu.id)}
                        onMouseLeave={() => !submenu.hoverItems && setActiveSubDropdown(null)}
                      >
                        <button
                          onClick={() => {
                            setActiveTab(item.id);
                            setActiveDropdown(null);
                            setActiveSubDropdown(null);
                          }}
                          className={`w-full flex items-center justify-between p-4 ${currentTheme.text} hover:bg-gray-50 transition-all duration-200 text-left group/item rounded-xl mx-1 my-1`}
                        >
                          <div className="flex items-center space-x-4">
                            <span className="text-2xl group-hover/item:scale-125 transition-transform duration-200">{submenu.icon}</span>
                            <div>
                              <div className="font-semibold group-hover/item:translate-x-1 transition-transform duration-200">{submenu.label}</div>
                              {submenu.description && (
                                <div className={`text-xs ${currentTheme.accent} mt-1`}>{submenu.description}</div>
                              )}
                            </div>
                          </div>
                          {submenu.hoverItems && (
                            <ChevronRight className="w-5 h-5 text-gray-400 group-hover/item:translate-x-1 transition-transform duration-200" />
                          )}
                        </button>

                        {/* Enhanced Sub-submenu */}
                        {submenu.hoverItems && (
                          <div className={`absolute top-0 left-full w-72 ${currentTheme.cardBg} rounded-2xl shadow-2xl border ${currentTheme.border} ml-3 transform transition-all duration-400 ${
                            activeSubDropdown === submenu.id 
                              ? 'opacity-100 visible translate-x-0 scale-100' 
                              : 'opacity-0 invisible -translate-x-4 scale-95'
                          }`}>
                            <div className="p-2">
                              {submenu.hoverItems.map((hoverItem, hoverIndex) => (
                                <button
                                  key={hoverItem.id}
                                  onClick={() => {
                                    setActiveTab(item.id);
                                    setActiveDropdown(null);
                                    setActiveSubDropdown(null);
                                  }}
                                  className={`w-full flex items-center space-x-3 p-3 ${currentTheme.text} hover:bg-gray-50 transition-all duration-200 text-left group/hover rounded-xl mx-1 my-1`}
                                >
                                  <span className="text-lg group-hover/hover:scale-125 transition-transform duration-200">{hoverItem.icon}</span>
                                  <div>
                                    <div className="font-medium group-hover/hover:translate-x-1 transition-transform duration-200">{hoverItem.label}</div>
                                    {hoverItem.description && (
                                      <div className={`text-xs ${currentTheme.accent} mt-1`}>{hoverItem.description}</div>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Enhanced decorative bottom border */}
        <div className="h-1 bg-gradient-to-r from-blue-400 via-purple-500 via-pink-500 to-orange-400 shadow-lg"></div>
      </nav>

      {/* Main Content */}
      <main className="p-6">
        {/* Dashboard Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {dashboardStats.map((stat, index) => (
            <StatCard key={index} stat={stat} />
          ))}
        </div>

        {activeTab === 'master' && (
          <div className="space-y-6">
            {/* Enhanced Controls */}
            <div className={`${currentTheme.cardBg} border ${currentTheme.border} rounded-2xl p-6 shadow-lg`}>
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex flex-wrap items-center gap-4">
                  <button
                    onClick={() => setShowAddForm(true)}
                    className={`flex items-center space-x-2 px-6 py-3 ${currentTheme.button} text-white rounded-xl transition-all duration-200 hover:shadow-lg transform hover:-translate-y-0.5 font-semibold`}
                  >
                    <Plus className="w-5 h-5" />
                    <span>Add Category</span>
                  </button>

                  <div className="relative">
                    <Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${currentTheme.accent}`} />
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`pl-12 pr-4 py-3 border ${currentTheme.border} ${currentTheme.cardBg} ${currentTheme.text} rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm w-80 transition-all duration-200 focus:shadow-md`}
                    />
                  </div>

                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center space-x-2 px-4 py-3 border ${currentTheme.border} ${currentTheme.text} rounded-xl hover:bg-gray-50 transition-all duration-200 font-medium`}
                  >
                    <Filter className="w-5 h-5" />
                    <span>Filters</span>
                  </button>

                  <div className="flex items-center space-x-2 border rounded-xl p-1">
                    <button
                      onClick={() => setViewMode('table')}
                      className={`p-2 rounded-lg transition-all duration-200 ${
                        viewMode === 'table' 
                          ? `${currentTheme.button} text-white` 
                          : `${currentTheme.text} hover:bg-gray-100`
                      }`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-lg transition-all duration-200 ${
                        viewMode === 'grid' 
                          ? `${currentTheme.button} text-white` 
                          : `${currentTheme.text} hover:bg-gray-100`
                      }`}
                    >
                      <Grid3X3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleRemoveSelected}
                    disabled={selectedItems.length === 0}
                    className={`flex items-center space-x-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold hover:shadow-lg transform hover:-translate-y-0.5`}
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Remove ({selectedItems.length})</span>
                  </button>

                  <button className={`flex items-center space-x-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-all duration-200 font-semibold hover:shadow-lg transform hover:-translate-y-0.5`}>
                    <Download className="w-4 h-4" />
                    <span>Export</span>
                  </button>
                </div>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">All Status</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                      <input 
                        type="date" 
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Item Count</label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option value="">All Ranges</option>
                        <option value="0-50">0-50 items</option>
                        <option value="51-200">51-200 items</option>
                        <option value="201+">200+ items</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Enhanced Table */}
            {viewMode === 'table' ? (
              <div className={`${currentTheme.cardBg} rounded-2xl shadow-xl overflow-hidden border ${currentTheme.border}`}>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={`${currentTheme.tableHead}`}>
                      <tr>
                        <th className="px-6 py-4 text-left">
                          <input
                            type="checkbox"
                            checked={selectedItems.length === paginatedCategories.length && paginatedCategories.length > 0}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                          />
                        </th>
                        <th className={`px-6 py-4 text-left text-sm font-bold ${currentTheme.tableHeadText} uppercase tracking-wider`}>S.No</th>
                        <th className={`px-6 py-4 text-left text-sm font-bold ${currentTheme.tableHeadText} uppercase tracking-wider`}>Category Code</th>
                        <th className={`px-6 py-4 text-left text-sm font-bold ${currentTheme.tableHeadText} uppercase tracking-wider`}>Category Name</th>
                        <th className={`px-6 py-4 text-left text-sm font-bold ${currentTheme.tableHeadText} uppercase tracking-wider`}>Items</th>
                        <th className={`px-6 py-4 text-left text-sm font-bold ${currentTheme.tableHeadText} uppercase tracking-wider`}>Status</th>
                        <th className={`px-6 py-4 text-left text-sm font-bold ${currentTheme.tableHeadText} uppercase tracking-wider`}>Created</th>
                        <th className={`px-6 py-4 text-left text-sm font-bold ${currentTheme.tableHeadText} uppercase tracking-wider`}>Remarks</th>
                        <th className={`px-6 py-4 text-left text-sm font-bold ${currentTheme.tableHeadText} uppercase tracking-wider`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className={`${currentTheme.cardBg} divide-y divide-gray-200`}>
                      {paginatedCategories.map((category, index) => (
                        <tr key={category.id} className="hover:bg-gray-50 transition-colors duration-150 group">
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={selectedItems.includes(category.id)}
                              onChange={(e) => handleSelectItem(category.id, e.target.checked)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                            />
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${currentTheme.text}`}>{category.sno}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200">
                              {category.categoryCode}
                            </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${currentTheme.text}`}>{category.categoryName}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 rounded-lg text-xs font-semibold bg-green-100 text-green-800">
                              {category.itemCount} items
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              category.status === 'Active' 
                                ? 'bg-green-100 text-green-800 border border-green-200' 
                                : 'bg-red-100 text-red-800 border border-red-200'
                            }`}>
                              {category.status}
                            </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${currentTheme.accent}`}>
                            {new Date(category.createdDate).toLocaleDateString()}
                          </td>
                          <td className={`px-6 py-4 text-sm ${currentTheme.accent} max-w-xs truncate`}>{category.remarks}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button className={`${currentTheme.accent} hover:bg-blue-50 p-2 rounded-lg transition-all duration-150 group-hover:scale-110`}>
                                <Eye className="w-4 h-4" />
                              </button>
                              <button className={`${currentTheme.accent} hover:bg-blue-50 p-2 rounded-lg transition-all duration-150 group-hover:scale-110`}>
                                <Edit className="w-4 h-4" />
                              </button>
                              <button className="relative p-2 rounded-lg text-gray-500 hover:bg-gray-50 transition-all duration-150 group-hover:scale-110">
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* Grid View */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {paginatedCategories.map((category) => (
                  <div key={category.id} className={`${currentTheme.cardBg} border ${currentTheme.border} rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 group hover:scale-105`}>
                    <div className="flex items-start justify-between mb-4">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(category.id)}
                        onChange={(e) => handleSelectItem(category.id, e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4 mt-1"
                      />
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        category.status === 'Active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {category.status}
                      </span>
                    </div>
                    
                    <div className="mb-4">
                      <h3 className={`text-lg font-bold ${currentTheme.text} mb-2`}>{category.categoryName}</h3>
                      <span className="px-3 py-1 rounded-lg text-xs font-bold bg-blue-100 text-blue-800">
                        {category.categoryCode}
                      </span>
                    </div>
                    
                    <div className="mb-4">
                      <p className={`text-sm ${currentTheme.accent} mb-2`}>{category.remarks}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{category.itemCount} items</span>
                        <span>{new Date(category.createdDate).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-end space-x-2">
                      <button className={`${currentTheme.accent} hover:bg-blue-50 p-2 rounded-lg transition-all duration-150`}>
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className={`${currentTheme.accent} hover:bg-blue-50 p-2 rounded-lg transition-all duration-150`}>
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="text-gray-500 hover:bg-gray-50 p-2 rounded-lg transition-all duration-150">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Enhanced Pagination */}
            <div className={`${currentTheme.cardBg} border ${currentTheme.border} rounded-2xl p-6 shadow-lg`}>
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className={`text-sm ${currentTheme.text} font-medium`}>
                  Showing <span className="font-bold text-blue-600">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="font-bold text-blue-600">{Math.min(currentPage * itemsPerPage, filteredCategories.length)}</span> of <span className="font-bold text-blue-600">{filteredCategories.length}</span> entries
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-lg border ${currentTheme.border} ${currentTheme.text} hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 font-medium`}
                  >
                    Previous
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`px-4 py-2 rounded-lg border transition-all duration-150 font-medium ${
                        currentPage === i + 1
                          ? `${currentTheme.button} text-white shadow-md`
                          : `${currentTheme.border} ${currentTheme.text} hover:bg-gray-50`
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 rounded-lg border ${currentTheme.border} ${currentTheme.text} hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 font-medium`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transaction' && (
          <div className={`${currentTheme.cardBg} rounded-2xl shadow-xl p-8 text-center border ${currentTheme.border}`}>
            <div className="mb-8">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-3xl mb-6 shadow-lg">
                📊
              </div>
              <h2 className={`text-3xl font-bold ${currentTheme.text} mb-4`}>Inventory Transactions</h2>
              <p className={`${currentTheme.accent} text-lg max-w-2xl mx-auto`}>
                Manage your inventory transactions with advanced features and real-time tracking
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {menuItems.find(item => item.id === 'transaction')?.submenus.map(submenu => (
                <button key={submenu.id} className={`p-6 rounded-2xl border ${currentTheme.border} hover:bg-gray-50 transition-all duration-200 hover:shadow-xl group hover:scale-105`}>
                  <div className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-200">{submenu.icon}</div>
                  <div className={`text-lg font-bold ${currentTheme.text} mb-2`}>{submenu.label}</div>
                  <div className={`text-sm ${currentTheme.accent}`}>{submenu.description}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'report' && (
          <div className={`${currentTheme.cardBg} rounded-2xl shadow-xl p-8 text-center border ${currentTheme.border}`}>
            <div className="mb-8">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center text-white text-3xl mb-6 shadow-lg">
                📈
              </div>
              <h2 className={`text-3xl font-bold ${currentTheme.text} mb-4`}>Reports & Analytics</h2>
              <p className={`${currentTheme.accent} text-lg max-w-2xl mx-auto`}>
                Generate comprehensive reports and gain valuable insights from your inventory data
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {menuItems.find(item => item.id === 'report')?.submenus.map(submenu => (
                <button key={submenu.id} className={`p-6 rounded-2xl border ${currentTheme.border} hover:bg-gray-50 transition-all duration-200 hover:shadow-xl group hover:scale-105`}>
                  <div className="text-3xl mb-4 group-hover:scale-110 transition-transform duration-200">{submenu.icon}</div>
                  <div className={`text-lg font-bold ${currentTheme.text} mb-2`}>{submenu.label}</div>
                  <div className={`text-sm ${currentTheme.accent}`}>{submenu.description}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Enhanced Add Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className={`${currentTheme.cardBg} rounded-3xl shadow-2xl w-full max-w-lg mx-4 transform transition-all duration-300 scale-100`}>
            <div className={`flex items-center justify-between p-8 border-b ${currentTheme.border} bg-gradient-to-r from-blue-50 to-purple-50 rounded-t-3xl`}>
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 ${currentTheme.button} rounded-2xl flex items-center justify-center text-white text-xl shadow-lg`}>
                  ➕
                </div>
                <div>
                  <h3 className={`text-2xl font-bold ${currentTheme.text}`}>Add New Category</h3>
                  <p className={`text-sm ${currentTheme.accent} mt-1`}>Create a new inventory category</p>
                </div>
              </div>
              <button
                onClick={() => setShowAddForm(false)}
                className={`p-3 rounded-full hover:bg-gray-100 transition-colors duration-200 ${currentTheme.text}`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-8 space-y-6">
              <div>
                <label className={`block text-sm font-bold ${currentTheme.text} mb-3`}>
                  Category Code *
                </label>
                <input
                  type="text"
                  required
                  value={formData.categoryCode}
                  onChange={(e) => setFormData({...formData, categoryCode: e.target.value})}
                  className={`w-full px-4 py-4 border ${currentTheme.border} ${currentTheme.cardBg} ${currentTheme.text} rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-medium`}
                  placeholder="Enter category code (e.g., ELEC001)"
                />
              </div>
              
              <div>
                <label className={`block text-sm font-bold ${currentTheme.text} mb-3`}>
                  Category Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.categoryName}
                  onChange={(e) => setFormData({...formData, categoryName: e.target.value})}
                  className={`w-full px-4 py-4 border ${currentTheme.border} ${currentTheme.cardBg} ${currentTheme.text} rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-medium`}
                  placeholder="Enter category name"
                />
              </div>
              
              <div>
                <label className={`block text-sm font-bold ${currentTheme.text} mb-3`}>
                  Remarks
                </label>
                <textarea
                  value={formData.remarks}
                  onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                  rows="4"
                  className={`w-full px-4 py-4 border ${currentTheme.border} ${currentTheme.cardBg} ${currentTheme.text} rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none font-medium`}
                  placeholder="Enter remarks or description"
                />
              </div>
              
              <div className="flex space-x-4 pt-6">
                <button
                  onClick={handleAddCategory}
                  disabled={!formData.categoryCode || !formData.categoryName}
                  className={`flex-1 py-4 px-6 ${currentTheme.button} text-white rounded-xl transition-all duration-200 font-bold hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <Plus className="w-5 h-5" />
                  <span>Add Category</span>
                </button>
                <button
                  onClick={() => setShowAddForm(false)}
                  className={`flex-1 py-4 px-6 border ${currentTheme.border} ${currentTheme.text} rounded-xl transition-all duration-200 font-bold hover:bg-gray-50`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryDashboard;